import React from 'react'
import { LoadableSelect } from '../../../../../components/Inputs'
import { InputLabel } from '../../../../../components/inputs/InputLabel'
import Client from '../../../../../client'

import config from '../../../../../config'
import {
  filterByConstraints,
  gatingAspect,
  SiblingValues
} from '../../../../products_v5/components/JoinedForm/EbayCard/constraints'

interface Props {
  categoryMarketCategoryId: any
  specificName: any
  createable: boolean
  value: string
  required: boolean
  onChange: any
  // Do we need this? I was too scared to delete it.
  /* eslint-disable react/no-unused-prop-types */
  allowDangerIntent?: boolean
  relevanceSearchCount?: number
  /* eslint-enable react/no-unused-prop-types */
  // Aspects that must be answered before this one can be offered, e.g. Size Type depends on Size.
  constrainedBy?: string[]
  // Every specific's current value, so cross-aspect compatibility can be resolved locally.
  siblingValues?: SiblingValues
}

export default class SpecificRenderer extends React.PureComponent<Props> {
  state = { valueIsIncompatible: false }

  // The aspect this one waits on, or null when it is ready to be answered.
  get gatedBy(): string | null {
    return gatingAspect(this.props.constrainedBy, this.props.siblingValues || {})
  }

  // LoadableSelect caches its options in local state after the first load, so it must be
  // remounted when a value it was filtered against changes. Keyed on only the constraining
  // aspects, so editing an unrelated specific does not refetch.
  get constraintKey(): string {
    const { constrainedBy, siblingValues } = this.props
    return (constrainedBy || []).map(name => `${name}=${siblingValues?.[name] ?? ''}`).join('|')
  }

  onChange = (v: { value: string; productRequired: boolean }) => {
    this.props.onChange(v && v.value, v && v.productRequired)
  }

  processRecommendations = d => {
    const { createable, value } = this.props
    const compatible = filterByConstraints(d.valueRecommendations || [], this.props.siblingValues || {})

    const incompatible = !!value && !!this.props.constrainedBy?.length && !compatible.some(vr => vr.value === value)
    // Guarded so processing options never triggers an avoidable re-render.
    if (this.state.valueIsIncompatible !== incompatible) this.setState({ valueIsIncompatible: incompatible })

    const values = compatible.map(vr => ({
      value: vr.value,
      label: vr.value,
      productRequired: (vr as any).validationRules?.productRequired === true
    }))

    // insert a created value, only if the input:
    // - is createable,
    // - has an actual value (not undefined),
    // - does not already exist in the list
    if (createable && value && !values.find(vr => vr.value === value)) {
      values.push({
        value,
        label: value,
        productRequired: false
      })
    }

    // A saved value the constraints now exclude is re-added so the user sees what must change.
    // Without this it would disappear from the form with no explanation.
    if (value && this.props.constrainedBy?.length && !values.find(vr => vr.value === value)) {
      const known = (d.valueRecommendations || []).find(vr => vr.value === value)
      if (known) values.push({ value, label: value, productRequired: false })
    }

    if (config.EBAY_AUTOFILL_SPECIFICS()) {
      if (value && !values.find(vr => vr.value === value)) {
        const correctlyCasedValue = values.find(vr => vr.value.toUpperCase() === value.toUpperCase())
        if (correctlyCasedValue) {
          // Their hammoq attributes had a valid value, but with the wrong case, like "floral" instead of "Floral". Fix it for them.
          this.onChange(correctlyCasedValue)
        } else {
          // This was provided with an invalid value (probably from a builder template), so remove it.
          // Because we have to make an API call per specific to get possible values to determine if autofilled specifics are valid,
          // this component is a convenient place to do this check.
          // We could get by without this. Amazingly, ebay's API will allow us to send up invalid values (and they display on the item page!)
          // even though free-text isn't allowed. But let's not depend on this.
          this.onChange(null)
        }
      }
    }

    return values
  }

  render() {
    const { categoryMarketCategoryId, required, specificName, value, createable } = this.props
    const { gatedBy } = this

    if (gatedBy) {
      return (
        <InputLabel className="loadable-select-label" title={specificName} optional={!required} required={required}>
          <div className="bp5-input bp5-disabled ebay-specific-gated">{`Select a ${gatedBy} first`}</div>
        </InputLabel>
      )
    }

    return (
      <LoadableSelect
        key={this.constraintKey}
        load={() => Client.categorySpecificRecommendations(categoryMarketCategoryId, specificName)}
        process={this.processRecommendations}
        simpleValue={false}
        onChange={this.onChange}
        title={specificName}
        virtualized
        autoload={value}
        optional={!required}
        required={required}
        value={value}
        createable={createable}
        placeholder={createable ? 'Create or Select' : 'Select'}
        intent={this.state.valueIsIncompatible && 'danger'}
        // react-select-same-height-as-blueprint - because often times these specifics will be on the same line
        // as a blueprint InputGroup, like for "Condition Description" or ConditionDescriptorPicker
        className="react-select-same-height-as-blueprint"
      />
    )
  }
}
